import React, { useEffect, useMemo, useState } from 'react'
import API from '../../../../../services/API'

import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import AddNewMedicineRow from '../../AddNewMedicineRow'
import MedicineStores from '../../MedicineStores'
import { Alert, Chip, Divider, Paper, Stack, Tab, Tabs } from '@mui/material'
import CustomDataGrid from '../../../../../components/DataGrid/CustomDataGrid'
import CheckCircle from '@mui/icons-material/CheckCircle'
import BigModal from '../../../../../components/Modals/BigModal'
import BagLevels from './BagLevels'
import AddIcon from '@mui/icons-material/Add'
import Inventory2Icon from '@mui/icons-material/Inventory2'
import LocalPharmacyIcon from '@mui/icons-material/LocalPharmacy'
import MedicationIcon from '@mui/icons-material/Medication'
import ScienceIcon from '@mui/icons-material/Science'
import TuneIcon from '@mui/icons-material/Tune'

const panelSx = {
  borderRadius: 2,
  border: '1px solid',
  borderColor: 'divider',
  boxShadow: '0 12px 28px rgba(17, 24, 39, 0.06)',
  overflow: 'hidden',
}

const StatTile = ({ icon, label, value, color = 'anBlue.main' }) => (
  <Stack
    direction="row"
    alignItems="center"
    justifyContent="space-between"
    gap={1.25}
    sx={{
      // border-box: minWidth 100% + padding/border otherwise overflows the
      // viewport on phones (no global box-sizing reset in this app)
      boxSizing: 'border-box',
      minWidth: { xs: '100%', sm: 180 },
      flex: 1,
      p: 1.5,
      border: '1px solid',
      borderColor: 'divider',
      borderRadius: 1,
      bgcolor: 'background.paper',
    }}
  >
    <Stack direction="row" alignItems="center" gap={1}>
      <Box
        sx={{
          width: 38,
          height: 38,
          borderRadius: 1,
          display: 'grid',
          placeItems: 'center',
          bgcolor: color,
          color: 'white',
        }}
      >
        {icon}
      </Box>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
    </Stack>

    <Typography variant="title3" lineHeight={1.1}>
      {value}
    </Typography>
  </Stack>
)

const SectionHeader = ({ title, subtitle, action }) => (
  <Stack
    direction={{ xs: 'column', md: 'row' }}
    alignItems={{ xs: 'stretch', md: 'center' }}
    justifyContent="space-between"
    gap={1.5}
    sx={{
      p: 2,
      bgcolor: '#F8FAFC',
      borderBottom: '1px solid',
      borderColor: 'divider',
    }}
  >
    <Box>
      <Typography variant="title3">{title}</Typography>
      <Typography variant="body2" color="text.secondary">
        {subtitle}
      </Typography>
    </Box>
    {action}
  </Stack>
)

const Medicines = () => {
  const [rows, setRows] = useState([])
  const [configuredLevels, setConfiguredLevels] = useState([])
  const [fetchError, setFetchError] = useState(false)
  const [editRow, setEditRow] = useState(null)
  const [activeSection, setActiveSection] = useState('formulary')

  useEffect(() => {
    API.get('/siteConfig/medicines')
      .then((res) => setRows(res.data))
      .catch(() => setFetchError(true))
    API.get('/settings?select=medsmanagement.bagLevels').then((res) => {
      const levels = res?.data?.medsmanagement?.bagLevels ?? []
      setConfiguredLevels(
        levels
          .map((level) => {
            if (typeof level === 'string') return level.trim()
            return level?.name?.trim?.() ?? ''
          })
          .filter(Boolean),
      )
    })
  }, [])

  const stats = useMemo(() => {
    return {
      medicines: rows.length,
      controlled: rows.filter((medicine) => medicine.isControlled).length,
      reusable: rows.filter((medicine) => medicine.reuse).length,
    }
  }, [rows])

  const tableHeight = Math.min(Math.max(rows.length * 52 + 120, 420), 680)

  return (
    <Stack gap={2.5}>
      <Paper
        sx={{
          borderRadius: 2,
          overflow: 'hidden',
          border: '1px solid',
          borderColor: 'divider',
          boxShadow: '0 18px 40px rgba(17, 24, 39, 0.08)',
        }}
      >
        <Box
          sx={{
            p: { xs: 2, md: 2.5 },
            bgcolor: '#0F3A5F',
            color: 'white',
          }}
        >
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            justifyContent="space-between"
            gap={2}
          >
            <Box>
              <Typography variant="title2" color="white">
                Medicines Configuration
              </Typography>
              <Typography sx={{ mt: 0.5, opacity: 0.84, color: 'white' }}>
                Maintain the formulary, storage locations, and bag capability
                levels used across medicines management. Use the tabs below to
                switch between each setup area; click a table row to edit it,
                then save changes from the panel or dialog that opens.
              </Typography>
            </Box>
          </Stack>
        </Box>

        <Stack
          direction={{ xs: 'column', md: 'row' }}
          gap={1.25}
          sx={{ p: 1.5, bgcolor: '#F8FAFC' }}
        >
          <StatTile
            icon={<MedicationIcon fontSize="small" />}
            label="Formulary items"
            value={stats.medicines}
          />
          <StatTile
            icon={<ScienceIcon fontSize="small" />}
            label="Controlled drugs"
            value={stats.controlled}
            color="#85276A"
          />
          <StatTile
            icon={<Inventory2Icon fontSize="small" />}
            label="Multidose items"
            value={stats.reusable}
            color="#239AA4"
          />
        </Stack>

        <Divider />

        <Tabs
          value={activeSection}
          onChange={(e, value) => setActiveSection(value)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            px: 1,
            minHeight: 48,
            '& .MuiTab-root': {
              minHeight: 48,
              textTransform: 'none',
              fontWeight: 700,
            },
          }}
        >
          <Tab
            icon={<LocalPharmacyIcon fontSize="small" />}
            iconPosition="start"
            value="formulary"
            label="Formulary"
          />
          <Tab
            icon={<Inventory2Icon fontSize="small" />}
            iconPosition="start"
            value="stores"
            label="Stores and bags"
          />
          <Tab
            icon={<TuneIcon fontSize="small" />}
            iconPosition="start"
            value="levels"
            label="Bag levels"
          />
        </Tabs>
      </Paper>

      {activeSection === 'formulary' && (
        <Paper sx={panelSx}>
          <SectionHeader
            title="Formulary"
            subtitle="Click a medicine to edit its presentation, legal basis, stock alarms, and bag levels."
            action={
              <Button
                startIcon={<AddIcon />}
                color="anGreen"
                variant="contained"
                size="small"
                onClick={() => setEditRow({})}
              >
                Add Medicine
              </Button>
            }
          />
          <Box sx={{ p: 2 }}>
            {fetchError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                Unable to load medicines. Please try again later.
              </Alert>
            )}
            <CustomDataGrid
              slots={{ footer: () => null }}
              hideToolbar
              disableRowSelectionOnClick
              rowHeight={64}
              sx={{ height: tableHeight }}
              onRowClick={({ row }) => {
                setEditRow(row)
              }}
              columns={[
                {
                  field: 'title',
                  headerName: 'Drug Name',
                  minWidth: 200,
                  flex: 1,
                },
                {
                  field: 'constitution',
                  headerName: 'Constitution',
                  minWidth: 150,
                },
                {
                  field: 'presentation',
                  headerName: 'Presentation',
                  minWidth: 150,
                },
                {
                  field: 'level',
                  headerName: 'Level',
                  minWidth: 150,
                  renderCell: ({ value }) => {
                    const values = value ?? []
                    const configuredKeys = configuredLevels.map((level) =>
                      level.toLowerCase(),
                    )
                    const valueKeys = values.map((level) =>
                      level?.toLowerCase?.(),
                    )
                    const coversAll =
                      configuredKeys.length > 0 &&
                      configuredKeys.every((level) => valueKeys.includes(level))

                    if (coversAll) return <Chip label="All" size="small" />

                    if (values.length > 2) {
                      return (
                        <Chip
                          label={`${values.length} levels`}
                          size="small"
                          variant="outlined"
                        />
                      )
                    }

                    return (
                      <Stack direction="row" gap={0.5} flexWrap="wrap" my={2}>
                        {values.map((level) => (
                          <Chip
                            key={level}
                            label={level}
                            size="small"
                            variant="outlined"
                          />
                        ))}
                      </Stack>
                    )
                  },
                  flex: 0.7,
                },
                { field: 'route', headerName: 'Route', minWidth: 150 },
                {
                  field: 'minNumberInStores',
                  headerName: 'Stores Alarm',
                  minWidth: 80,
                  align: 'center',
                  headerAlign: 'center',
                },
                {
                  field: 'minNumberInBags',
                  headerName: 'Bag Alarm',
                  minWidth: 80,
                  align: 'center',
                  headerAlign: 'center',
                },
                {
                  field: 'reuse',
                  minWidth: 150,
                  headerName: 'Multiple Doses',
                  renderCell: ({ value }) =>
                    value && <CheckCircle color="success" />,
                  align: 'center',
                  headerAlign: 'center',
                },
                {
                  field: 'additionalTraining',
                  headerName: 'Additional Training',
                  minWidth: 150,
                  renderCell: ({ value }) =>
                    value && <CheckCircle color="success" />,
                  align: 'center',
                  headerAlign: 'center',
                },
                {
                  field: 'legalBasis',
                  headerName: 'Legal Basis',
                  minWidth: 150,
                  renderCell: ({ value }) =>
                    value && <Chip label={value} size="small" />,
                },
              ]}
              rows={rows}
              getRowId={(row) => row._id}
            />
            <BigModal
              style={{
                width: '50%',
                maxWidth: '90vw',
              }}
              open={!!editRow}
              onClose={() => {
                setEditRow(null)
              }}
            >
              <AddNewMedicineRow
                setRows={setRows}
                existingRow={editRow}
                onClose={() => setEditRow(null)}
              />
            </BigModal>
          </Box>
        </Paper>
      )}

      {activeSection === 'stores' && <MedicineStores />}
      {activeSection === 'levels' && <BagLevels />}
    </Stack>
  )
}

export default Medicines
